import { useNavigate, useParams } from 'react-router-dom';
import { CheckIcon, CopyPlusIcon, RotateCcwIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { PostPreviewPanel } from './editor/PostPreviewPanel';
import { PostPlanningPanel } from './editor/PostPlanningPanel';
import { PostTopicPanel } from './editor/PostTopicPanel';
import { PostImagePanel } from './editor/PostImagePanel';
import { PostTextPanel } from './editor/PostTextPanel';
import { useAdmin } from '../AdminContext';
import { useSocialPosts } from './useSocialPosts';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { marketingTokens } from '../marketing/marketingRules';
import { errorCount } from '../editing';
import { captionLimit, instagramHandle, postChecks, postText } from './socialRules';
import { fromIso, iso, longDate, startOfWeek } from '../../core/format/dates';
import type { SocialPost } from '../../core/types/social';

/** Eén post voorbereiden: beeld, tekst, kanalen en planning, met live voorbeeld. De vlakken staan in ./editor/. */
export function SocialEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    marketing,
    site,
    index
  } = useAdmin();
  const {
    posts,
    updatePost,
    removePost,
    duplicatePost
  } = useSocialPosts();
  const post = posts.find(p => p.id === id);
  if (!post) {
    return <MissingItem title="Post niet gevonden" back={{
      to: '/be/social',
      label: 'Social planner'
    }} data-id="element-4691" data-mp="3mszzklc4uhdc" />;
  }
  const set = (patch: Partial<SocialPost>) => updatePost(post.id, patch);
  const tokens = marketingTokens(site);
  const design = post.designId ? marketing.designs.find(d => d.id === post.designId) : undefined;
  const checks = postChecks(post, tokens, design, index.productById);
  const text = postText(post, tokens);
  const errors = errorCount(checks);
  const backTo = post.date ? `/be/social?week=${iso(startOfWeek(fromIso(post.date)))}` : '/be/social?weergave=lijst';
  const markPosted = () => {
    set({
      status: 'geplaatst'
    });
    toast.success('Afgevinkt als geplaatst', {
      description: 'Mooi. Hij staat grijs in de planner.'
    });
  };
  return <div data-id="element-4692" data-mp="3gy9qd99eeleb">
      <PageHeader title={post.title || 'Naamloze post'} description={post.date ? `${longDate(fromIso(post.date))} om ${post.time}` : 'Nog zonder datum'} back={{
      to: backTo,
      label: 'Social planner'
    }} actions={<>
            <button type="button" onClick={() => duplicatePost(post)} className={btnSecondary} data-id="element-4694" data-mp="3vv0w009o0tcd">
              <CopyPlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4695" data-mp="3vt65m6fmk90w" />
              Dupliceer
            </button>
            {post.status === 'geplaatst' ? <button type="button" onClick={() => set({
        status: 'klaar'
      })} className={btnSecondary} data-id="element-4696" data-mp="21iswsaqb9qas">
                <RotateCcwIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4697" data-mp="25knvd7guxpkv" />
                Toch niet geplaatst
              </button> : <button type="button" onClick={markPosted} disabled={errors > 0} aria-describedby={errors ? 'post-blokkade' : undefined} className={btnPrimary} data-id="element-4698" data-mp="1t7k48gcb3lzd">
                <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-4699" data-mp="2gxy1cl9r0b0t" />
                Geplaatst
              </button>}
          </>} data-id="element-4693" data-mp="3gyryp49c4q2l" />
      {errors > 0 && post.status !== 'geplaatst' &&
    // Een knop die uit staat zonder uitleg is een doodlopende weg; vooral op de telefoon, waar
    // "Klaar om te posten?" pas onder het voorbeeld staat.
    <p id="post-blokkade" className="-mt-3 mb-6 text-[13px] text-muted sm:-mt-5" data-id="element-4700" data-mp="wi30w80sqc0u">
          Nog {errors === 1 ? '1 punt' : `${errors} punten`} op te lossen voordat je kunt afvinken; zie “Klaar om te posten?”.
        </p>}

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-4701" data-mp="2fffrv01zoq7q">
        <div className="min-w-0 lg:col-span-5" data-id="element-4702" data-mp="2fdlafvpxfg8e">
          <PostPreviewPanel post={post} design={design} tokens={tokens} text={text} checks={checks} avatar={site.founder.portrait} handle={instagramHandle(site.salon.instagram, site.salon.name)} data-id="element-4703" data-mp="2k6o6qbm393sa" />
        </div>

        <div className="min-w-0 space-y-6 lg:col-span-7" data-id="element-4704" data-mp="2y78or5k6ivkq">
          <PostPlanningPanel post={post} set={set} data-id="element-4705" data-mp="335a3xfehiaqx" />
          <PostTopicPanel post={post} set={set} data-id="element-4706" data-mp="333yyzc9aywy8" />
          <PostImagePanel post={post} design={design} set={set} data-id="element-4707" data-mp="2p5hfxruce8mn" />
          <PostTextPanel post={post} set={set} textLength={text.length} limit={captionLimit(post)} data-id="element-4708" data-mp="2p57tge8iajlp" />

          <Panel title="Notities" data-id="element-4709" data-mp="2u2r7pe57ewdn">
            <label className="sr-only" htmlFor="post-notes" data-id="element-4710" data-mp="1dqlsphhmqyuh">
              Notities
            </label>
            <textarea id="post-notes" value={post.notes} onChange={e => set({
            notes: e.target.value
          })} rows={3} placeholder="Wat wil je filmen, wie moet je nog vragen…" className={`${inputCls} resize-y leading-relaxed`} data-id="element-4711" data-mp="1ipyes00og1w0" />
            <div className="mt-6 border-t border-line pt-4" data-id="element-4712" data-mp="1k5j90h3x5tu0">
              <DeleteButton undoable label="Post verwijderen" onConfirm={() => {
              const restore = removePost(post.id);
              navigate(backTo);
              undoToast(`“${post.title || 'Naamloze post'}” verwijderd`, restore, {
                description: 'Pas definitief als je opslaat.',
                restored: 'Post teruggezet'
              });
            }} data-id="element-4713" data-mp="1f7hpjv2kzsi0" />
            </div>
          </Panel>
        </div>
      </div>
    </div>;
}